import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';

import { XprtDataService } from '../xprt-data/xprt-data.service';

import { Observable, rx, observableOf } from '@shared/rx';

import { isEmpty, isEqual, map, find, filter, uniq, isFunction } from 'lodash';
import { ICommonSimpleOperator, ICompanyUserType } from '@app/shared/types';
import { lazyInject } from '@app/containers/base/decorators';
import { StaffDataService } from '../staff-data';
import { IStaffUserInfo } from '../staff-data/staff-data.types';
import { UtilityServiceProvider } from '../datasource/utility-service-provider.service';

@Injectable()
export class OperatorService {
  @lazyInject(UtilityServiceProvider)
  public utilityServiceProvider: UtilityServiceProvider;

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public fetchOperators(
    operatorLogs: ICommonSimpleOperator[],
  ): Observable<ICommonSimpleOperator[]> {
    if (isEmpty(operatorLogs)) {
      return observableOf([]);
    }

    const operatorIds = uniq(
      filter(
        map(operatorLogs, (operator) => operator.id),
        Boolean,
      ),
    );

    const xprtDataService: XprtDataService =
      this.utilityServiceProvider.getUtilityService(
        XprtDataService.UTILITY_TYPE,
      );

    const staffDataService: StaffDataService =
      this.utilityServiceProvider.getUtilityService(
        StaffDataService.UTILITY_TYPE,
      );

    return xprtDataService
      .getUsersInfoByFilter({
        filter: `_id eq in(${operatorIds.join()})`,
      })
      .pipe(
        rx.map((userInfos: ICompanyUserType[]) => {
          const updatedUserInfos: ICommonSimpleOperator[] = map(
            operatorLogs,
            (log) => {
              const matchedUser = find(userInfos, (user) => user.id === log.id);

              return { ...log, name: matchedUser?.name };
            },
          );

          const undefinedNamesIds = uniq(
            map(
              filter(updatedUserInfos, (user) => !user?.name),
              'id',
            ),
          );

          return [updatedUserInfos, undefinedNamesIds];
        }),
        rx.switchMap(
          ([updatedUserInfos, undefinedNamesIds]: [
            ICommonSimpleOperator[],
            string[] | undefined,
          ]) => {
            if (isEmpty(undefinedNamesIds)) {
              return observableOf(updatedUserInfos);
            }

            return staffDataService.batchGetUserInfo(undefinedNamesIds).pipe(
              rx.switchMap((allResults) => {
                const updatedEmployeeNameInfos = map(
                  updatedUserInfos,
                  (userInfo) => {
                    const matchedUser = find(
                      allResults,
                      (user: IStaffUserInfo) =>
                        isEqual(user.EmployeeId, userInfo.id),
                    );

                    return {
                      id: userInfo.id,
                      name: matchedUser?.Name ?? userInfo?.name,
                    };
                  },
                );

                return observableOf(updatedEmployeeNameInfos);
              }),
            );
          },
        ),
        rx.catchError((_) => observableOf(operatorLogs)),
      );
  }
}
